/** Типи дублюють серверні вручну: панель навмисно не імпортує з ../src. */

export type ResponseStatus = 'ANSWERED' | 'FALLBACK' | 'ESCALATED' | 'ERROR';
export type Channel = 'WEB' | 'TELEGRAM' | 'TEAMS' | 'ODOO';
export type IndexingStatus =
  | 'PENDING'
  | 'PARSING'
  | 'CHUNKING'
  | 'EMBEDDING'
  | 'INDEXING'
  | 'VALIDATING'
  | 'ACTIVE'
  | 'FAILED'
  /** Витіснена новішою версією: файл на місці, у пошуку її немає. */
  | 'SUPERSEDED';

export type ContactLinkKind = 'TEAMS_CHAT' | 'EMAIL' | 'PHONE' | 'WEB';

/** Посилання з тіла документа: у тексті лишилась назва, адреса приходить окремо. */
export interface ContactLink {
  label: string;
  url: string;
  kind: ContactLinkKind;
}

export interface SourceReference {
  chunkId: string;
  documentId: string;
  documentVersionId: string;
  title?: string;
  page?: number;
  section?: string;
  businessVersion?: string;
  fileName?: string;
  snippet?: string;
  url?: string;
  links?: ContactLink[];
}

export interface QueryResponse {
  status: ResponseStatus;
  /** Markdown: підставлені посилання вже стоять у тексті. */
  answer: string | null;
  sources: SourceReference[];
  /** Посилання, для яких у тексті не знайшлося назви — показуємо списком. */
  links: ContactLink[];
  fallbackReason: string | null;
  requestId: string;
  /** `null` — сервер не дав розмові власника: ні адреси, ні продовження. */
  conversationId: string | null;
}

export type MessageRole = 'USER' | 'ASSISTANT' | 'SYSTEM' | 'ADMIN';

/** Рядок списку розмов: GET /assistant/conversations. */
export interface ConversationSummary {
  id: string;
  /** Перше питання людини, обрізане сервером: власної назви розмова не має. */
  title: string;
  updatedAt: string;
  messageCount: number;
}

/** Курсорна сторінка: `nextCursor === null` — далі нічого немає. */
export interface ConversationPage {
  items: ConversationSummary[];
  nextCursor: string | null;
}

/** Одне повідомлення історії: GET /assistant/conversations/:id і .../unanswered-questions/:id/thread. */
export interface ConversationMessage {
  id: string;
  role: MessageRole;
  content: string;
  createdAt: string;
  /** Лише в повідомлень бота; у питання людини статусу немає. */
  responseStatus: ResponseStatus | null;
  /** Знімок цитат на момент відповіді; `null` у записів, старших за цю колонку. */
  sources: SourceReference[] | null;
  /** За ним панель відкидає локальні ходи, що вже прийшли з сервера. */
  requestId: string | null;
  /** Підпис адміна, лише для `role: 'ADMIN'`. */
  authorLabel: string | null;
}

export interface ConversationDetail {
  id: string;
  title: string;
  createdAt: string;
  messages: ConversationMessage[];
}

/** Ключ саме `versionId`, не `id`: так сервер називає версію в усіх відповідях. */
export interface DocumentVersionSummary {
  versionId: string;
  businessVersion: string;
  indexingStatus: IndexingStatus;
  indexingError: string | null;
  indexedAt: string | null;
  fileName: string;
  fileSize: number | null;
  chunkCount: number;
  /** Відносне посилання на файл: /documents/{id}/versions/{vid}/file */
  url: string;
}

export interface DocumentSummary {
  id: string;
  title: string;
  sourceType: string | null;
  language: string;
  domain: string;
  knowledgeBase: string;
  createdAt: string;
  versionCount: number;
  versions: DocumentVersionSummary[];
}

/** Запис документа, ще без файлу. */
export interface CreatedDocument {
  id: string;
  title: string;
  knowledgeBaseId: string;
}

/** Байти збережено, версія вже в черзі на індексацію. */
export interface UploadedVersion {
  versionId: string;
  bucket: string;
  objectKey: string;
  size: number;
}

/** Розмір, після якого сервер відмовить: MAX_UPLOAD_BYTES у documents.controller.ts. */
export const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;

export interface ReindexResult {
  versionId: string;
  indexingStatus: string;
}

/** Що зняв каскад видалення документа. */
export interface DeleteDocumentResult {
  documentId: string;
  versionsRemoved: number;
  chunksRemoved: number;
}

export type QueueState = 'all' | 'open' | 'resolved';

export interface UnansweredQuestion {
  id: string;
  requestId: string;
  question: string;
  channel: Channel;
  reason: string;
  selectedDomains: string[];
  createdAt: string;
  resolvedAt: string | null;
  resolvedById: string | null;
  resolvedByLabel: string | null;
  conversationId: string | null;
}

export interface ResolveResult {
  changed: number;
}

/** GET /admin/unanswered-questions/:id/thread — саме питання, без розмови. */
export interface UnansweredThreadQuestion {
  id: string;
  question: string;
  reason: string;
  channel: Channel;
  requestId: string;
  createdAt: string;
  resolvedAt: string | null;
  resolvedById: string | null;
  resolvedByLabel: string | null;
}

export interface UnansweredThreadConversation {
  id: string;
  createdAt: string;
  messages: ConversationMessage[];
  /** Нитка довша за вікно навколо питання — показано не всю. */
  truncated: boolean;
}

export interface UnansweredThread {
  question: UnansweredThreadQuestion;
  /** `null` — легасі-рядок без conversationId, переписки не було де зберігати. */
  conversation: UnansweredThreadConversation | null;
}

export interface ReplyResult {
  messageId: string;
}

/** Ролі з бекенда (src/common/auth/principal.ts). */
export const ADMIN_ROLE = 'ADMIN';
export const SUPERADMIN_ROLE = 'SUPERADMIN';

/** Роли, які суперадмін може видати (SERVICE належить ключу, не людині). */
export const ASSIGNABLE_ROLES = [ADMIN_ROLE, SUPERADMIN_ROLE] as const;

/** GET /auth/me — хто зробив запит. */
export interface Principal {
  kind?: 'SERVICE' | 'USER';
  id?: string;
  /** Лише справжнє імʼя — сервер не підставляє тут пошту. */
  displayName?: string;
  email?: string;
  roles: string[];
}

export interface LoginResult {
  id: string;
  email: string;
  displayName: string | null;
  roles: string[];
  expiresAt: string;
}

export type UserStatus = 'PENDING' | 'ACTIVE' | 'DISABLED';

export interface PanelUser {
  id: string;
  email: string;
  displayName: string | null;
  status: UserStatus;
  roles: string[];
  createdAt: string;
  lastLoginAt: string | null;
}

/** URL показується РІВНО один раз: сирий токен не зберігається. */
export interface ResetLink {
  url: string;
  expiresAt: string;
}

/** `resetLink` = null для неухваленого акаунта — увійти він однаково не зможе. */
export type CreatedPanelUser = PanelUser & { resetLink: ResetLink | null };

export function hasRole(principal: Principal | null, role: string): boolean {
  if (!principal) return false;
  if (principal.roles.includes(SUPERADMIN_ROLE)) return true;
  return principal.roles.includes(role);
}

/** Одна зміна вимикача бота: журнал відповідає «хто, коли, чому і з якого стану». */
export interface BotStateChange {
  id: string;
  enabled: boolean;
  previousEnabled: boolean;
  reason: string;
  actorId: string;
  /** NULL, коли в акаунті немає імені — тоді лишається тільки `actorId`. */
  actorLabel: string | null;
  createdAt: string;
}

export interface BotState {
  enabled: boolean;
  /** Найсвіжіші першими; `history[0]` — та сама зміна, що дала `enabled`. */
  history: BotStateChange[];
}

/** `changed: false` — стан уже був таким, нового рядка в журналі немає. */
export interface BotStateResult extends BotState {
  changed: boolean;
}

/** `skipped` — компонент не задіяний за цієї конфігурації. */
export interface ComponentHealth {
  status: 'up' | 'down' | 'skipped';
  error?: string;
  detail?: string;
}

export interface HealthReport {
  status: 'ok' | 'degraded';
  info: Record<string, ComponentHealth>;
  checkedAt: string;
}

export interface Paged<T> {
  items: T[];
  total: number;
  limit?: number;
  offset?: number;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Текст помилки для екрана: у `ApiError` він уже людський, решта — несподіванка. */
export function errorText(error: unknown, fallback = 'Спробуйте ще раз.'): string {
  return error instanceof ApiError ? error.message : fallback;
}

/** Тут 401 — очікувана відповідь: інакше екран входу перекидав би сам на себе. */
const UNAUTHENTICATED_PATHS = ['/auth/me', '/auth/login', '/auth/set-password'];

/** Виставляє router.tsx: модуль не знає про навігацію і не має її імпортувати. */
let onSessionLost: (() => void) | undefined;

export function setSessionLostHandler(handler: () => void): void {
  onSessionLost = handler;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response;
  try {
    response = await fetch(path, {
      ...init,
      // Кука їде сама: same-origin і в розробці, і в проді. Ключів у бандлі не тримаємо.
      credentials: 'same-origin',
      headers: { Accept: 'application/json', ...(init?.headers ?? {}) },
    });
  } catch {
    // fetch кидає лише на транспортній помилці — сервер не відповів узагалі.
    throw new ApiError(0, 'Немає зв’язку з сервером. Спробуйте за хвилину.');
  }

  if (response.status === 401 && !UNAUTHENTICATED_PATHS.some((p) => path.startsWith(p))) {
    onSessionLost?.();
  }

  if (!response.ok) {
    throw new ApiError(response.status, await errorMessage(response));
  }
  if (response.status === 204) {
    return undefined as T;
  }
  return (await response.json()) as T;
}

/** Nest віддає {message}, а помилка від проксі — ні. */
async function errorMessage(response: Response): Promise<string> {
  const fallback = 'Не вдалося виконати запит.';
  try {
    const body: unknown = await response.json();
    if (body && typeof body === 'object' && 'message' in body) {
      const { message } = body as { message: unknown };
      if (typeof message === 'string') return message;
      if (Array.isArray(message)) return message.join('; ');
    }
  } catch {
    /* не JSON — лишаємо запасний текст */
  }
  return fallback;
}

function query(params: Record<string, string | number | boolean | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') search.set(key, String(value));
  }
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

export const api = {
  // ---- Вхід і сесія -------------------------------------------------------

  login(email: string, password: string): Promise<LoginResult> {
    return request<LoginResult>('/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
  },

  logout(): Promise<void> {
    return request<void>('/auth/logout', { method: 'POST' });
  },

  /** На 401 повертає null, а не кидає: «не увійшов» — нормальний стан, не збій. */
  async me(): Promise<Principal | null> {
    try {
      return await request<Principal>('/auth/me');
    } catch (error) {
      if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
        return null;
      }
      throw error;
    }
  },

  /** Без входу: перепусткою є сам токен із листа. */
  setPassword(token: string, password: string): Promise<void> {
    return request<void>('/auth/set-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, password }),
    });
  },

  // ---- Асистент і база знань ----------------------------------------------

  /** Без `conversationId` сервер починає нову розмову й повертає її id. */
  ask(question: string, conversationId?: string): Promise<QueryResponse> {
    return request<QueryResponse>('/assistant/query', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ question, channel: 'WEB', conversationId }),
    });
  },

  /** Найсвіжіші першими; сервер віддає лише власні розмови. */
  listConversations(params: { take?: number; cursor?: string } = {}): Promise<ConversationPage> {
    return request<ConversationPage>(`/assistant/conversations${query(params)}`);
  },

  /** Чужа або неіснуюча розмова — 404, а не 403. */
  getConversation(id: string): Promise<ConversationDetail> {
    return request<ConversationDetail>(`/assistant/conversations/${encodeURIComponent(id)}`);
  },

  /** `allVersions` — у `versions` приїде вся історія, а не лише найсвіжіша версія. */
  listDocuments(
    params: {
      limit?: number;
      offset?: number;
      search?: string;
      indexingStatus?: string;
      allVersions?: boolean;
    } = {},
  ): Promise<Paged<DocumentSummary>> {
    return request<Paged<DocumentSummary>>(`/documents${query(params)}`);
  },

  createDocument(title: string): Promise<CreatedDocument> {
    return request<CreatedDocument>('/documents', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title }),
    });
  },

  /** `Content-Type` не задаємо: multipart boundary додає лише сам браузер. */
  uploadVersion(documentId: string, file: File): Promise<UploadedVersion> {
    const body = new FormData();
    body.append('file', file);
    return request<UploadedVersion>(`/documents/${documentId}/versions/upload`, {
      method: 'POST',
      body,
    });
  },

  reindex(documentId: string, versionId: string): Promise<ReindexResult> {
    return request<ReindexResult>(`/documents/${documentId}/versions/${versionId}/reindex`, {
      method: 'POST',
    });
  },

  deleteDocument(documentId: string): Promise<DeleteDocumentResult> {
    return request<DeleteDocumentResult>(`/documents/${documentId}`, { method: 'DELETE' });
  },

  listUnanswered(
    params: {
      limit?: number;
      offset?: number;
      search?: string;
      reason?: string;
      days?: number;
      state?: QueueState;
    } = {},
  ): Promise<Paged<UnansweredQuestion>> {
    return request<Paged<UnansweredQuestion>>(`/admin/unanswered-questions${query(params)}`);
  },

  /** `resolved: false` знімає позначку — той самий маршрут, інший напрям. */
  resolveUnanswered(ids: string[], resolved: boolean): Promise<ResolveResult> {
    return request<ResolveResult>('/admin/unanswered-questions/resolve', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids, resolved }),
    });
  },

  getUnansweredThread(id: string): Promise<UnansweredThread> {
    return request<UnansweredThread>(
      `/admin/unanswered-questions/${encodeURIComponent(id)}/thread`,
    );
  },

  replyToUnanswered(id: string, content: string): Promise<ReplyResult> {
    return request<ReplyResult>(`/admin/unanswered-questions/${encodeURIComponent(id)}/reply`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ content }),
    });
  },

  getBotState(): Promise<BotState> {
    return request<BotState>('/admin/bot-state');
  },

  /** Причина обовʼязкова в обидва боки: журнал без неї не пояснює нічого. */
  setBotState(enabled: boolean, reason: string): Promise<BotStateResult> {
    return request<BotStateResult>('/admin/bot-state', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled, reason }),
    });
  },

  health(): Promise<HealthReport> {
    return request<HealthReport>('/health');
  },

  listUsers(status?: UserStatus): Promise<PanelUser[]> {
    return request<PanelUser[]>(`/auth/users${query({ status })}`);
  },

  /** Пароля не приймає: у відповіді приходить одноразове посилання для власника. */
  createUser(input: {
    email: string;
    displayName?: string;
    status?: 'PENDING' | 'ACTIVE';
    roles?: string[];
  }): Promise<CreatedPanelUser> {
    return request<CreatedPanelUser>('/auth/users', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
  },

  setUserStatus(id: string, status: UserStatus): Promise<PanelUser> {
    return request<PanelUser>(`/auth/users/${id}/status`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    });
  },

  createResetLink(id: string): Promise<ResetLink> {
    return request<ResetLink>(`/auth/users/${id}/reset-link`, { method: 'POST' });
  },
};
